import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { settings } from '@/lib/db';

import { useShouldShowWelcome, WELCOME_SEEN_KEY } from './first-run';

/**
 * The first-run guide: the order things have to happen in for the rest of the
 * app to work, shown once over whatever page the pilot landed on.
 *
 * The order is the point. A track imported before its aircraft exists cannot
 * find a Class for its logbook draft, and a fetch needs an aircraft (and its
 * ICAO address) to search for. Nothing on any single page says that, because
 * every page is usable on its own — so it is said once, here, and then gets
 * out of the way.
 *
 * "Seen" is a row in the settings table rather than `localStorage`, for two
 * reasons. A factory reset clears every table (`resetDatabase`), so the guide
 * comes back after a reset with no extra wiring — a reset browser is a first
 * run again. And `reset.ts` promises that nothing outside IndexedDB holds app
 * state; a flag in `localStorage` would be the first exception.
 *
 * It also stays shut on a database that already has aircraft or tracks in it.
 * Without that, every existing install would have opened to a guide about
 * adding the aircraft it already has the first time this shipped.
 */

/*
 * Three steps, in the order a pilot actually meets them. An API key used to be
 * step two: optional, needed by few, and placed ahead of importing a file —
 * the thing every pilot does first. Fetching from an API is now a clause on
 * the import step, and its setup lives in Settings where it always did.
 */
const STEPS: readonly { title: string; body: string }[] = [
  {
    title: 'Add an aircraft',
    body: 'Enter the registration. NZ registrations fill in the type, class and ICAO address from the CAA register.',
  },
  {
    title: 'Import flights',
    body: 'Drop FR24 KML, KMZ or CSV files, or GPX or IGC logs, on any page. Flights with no export can be fetched from an API.',
  },
  {
    title: 'Approve logbook entries',
    body: 'Each imported flight creates a draft entry. Totals count approved entries only.',
  },
];

function markSeen() {
  void settings.set(WELCOME_SEEN_KEY, true);
}

/**
 * `deferred` holds the guide back while another dialog is up — the storage
 * notice, on a browser that deletes data on a timer — so the two never stack.
 */
export function WelcomeDialog({ deferred = false }: { deferred?: boolean }) {
  const show = useShouldShowWelcome();
  const ref = useRef<HTMLDialogElement>(null);

  /*
   * A native `<dialog>` opened with `showModal()` rather than a positioned div:
   * the top layer needs no z-index from the layer scale, the rest of the page
   * goes inert, focus is trapped, and Escape closes it — all without a
   * dependency. Escape fires `close`, which is handled below like the button.
   *
   * Opening only, never closing. A file dropped while the guide is up makes
   * `show` false the moment its track lands, and a guide that vanished on its
   * own mid-read would look like a crash. It closes when the pilot closes it.
   */
  useEffect(() => {
    const dialog = ref.current;
    if (show === true && !deferred && dialog !== null && !dialog.open) {
      dialog.showModal();
    }
  }, [show, deferred]);

  const close = () => {
    ref.current?.close();
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby="welcome-title"
      onClose={markSeen}
      className="border-border bg-surface text-foreground rounded-container backdrop:bg-foreground/40 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto border p-0 shadow-lg print:hidden"
    >
      <div className="p-5 sm:p-6">
        <h2 id="welcome-title" className="text-base font-semibold tracking-tight">
          Getting started
        </h2>

        <ol className="mt-4 space-y-4">
          {STEPS.map(({ title, body }, index) => (
            <li key={title} className="flex gap-3">
              <span
                aria-hidden="true"
                className="bg-accent-soft text-foreground tabular rounded-control flex size-6 shrink-0 items-center justify-center text-xs font-medium"
              >
                {index + 1}
              </span>
              <div>
                <h3 className="text-sm font-medium">{title}</h3>
                <p className="text-muted-foreground mt-0.5 text-sm leading-relaxed">{body}</p>
              </div>
            </li>
          ))}
        </ol>

        {/*
          Said once, here, because the one way to lose everything in a
          local-first app is not knowing it is local. Not a step: a pilot
          with one device can ignore it.
        */}
        <p className="text-muted-foreground border-border mt-5 border-t pt-4 text-xs leading-relaxed">
          Stored locally on this device. A{' '}
          <Link
            to="/settings"
            hash="sync"
            onClick={close}
            className="hover:text-foreground underline underline-offset-2"
          >
            sync folder
          </Link>{' '}
          keeps a copy in iCloud Drive, OneDrive or Dropbox.
        </p>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            Close
          </Button>
          <Button asChild>
            <Link to="/aircraft/new" onClick={close}>
              Add aircraft
            </Link>
          </Button>
        </div>
      </div>
    </dialog>
  );
}
